import { z } from "zod";
import { CONNECTIONS } from "../constants";
import { nonNegativeCents } from "./common";

// ---------------------------------------------------------------------------
// Authority-level settings

export const lengthClassSchema = z.object({
  key: z.string().min(1).max(20),
  /** Upper bound in metres (inclusive). `null` = open-ended last class. */
  maxM: z.number().positive().max(200).nullable(),
  typicalBeamM: z.number().positive().max(30),
  typicalLengthM: z.number().positive().max(200),
  typicalDraftM: z.number().min(0).max(15),
});
export type LengthClass = z.infer<typeof lengthClassSchema>;

export const DEFAULT_LENGTH_CLASSES: LengthClass[] = [
  { key: "lt8", maxM: 8, typicalBeamM: 2.8, typicalLengthM: 7, typicalDraftM: 1.0 },
  { key: "8to12", maxM: 12, typicalBeamM: 3.6, typicalLengthM: 10.5, typicalDraftM: 1.6 },
  { key: "12to15", maxM: 15, typicalBeamM: 4.3, typicalLengthM: 13.5, typicalDraftM: 2.0 },
  { key: "15to20", maxM: 20, typicalBeamM: 5.2, typicalLengthM: 17.5, typicalDraftM: 2.4 },
  { key: "gt20", maxM: null, typicalBeamM: 6.5, typicalLengthM: 24, typicalDraftM: 2.8 },
];

export const authoritySettingsSchema = z.object({
  lengthClasses: z
    .array(lengthClassSchema)
    .min(1)
    .max(10)
    .refine(
      (classes) =>
        classes.every((c, i) => i === classes.length - 1 || (c.maxM !== null && (i === 0 || c.maxM > (classes[i - 1]!.maxM ?? 0)))),
      { message: "error.validation" },
    ),
  defaultGapM: z.number().min(0).max(5),
  depthMarginM: z.number().min(0).max(3),
});
export type AuthoritySettings = z.infer<typeof authoritySettingsSchema>;

export const DEFAULT_AUTHORITY_SETTINGS: AuthoritySettings = {
  lengthClasses: DEFAULT_LENGTH_CLASSES,
  defaultGapM: 0.5,
  depthMarginM: 0,
};

export const vatSettingsSchema = z.object({
  inVatSystem: z.boolean(),
  defaultRatePercent: z.number().min(0).max(50),
});
export type VatSettings = z.infer<typeof vatSettingsSchema>;

export const fiscalSettingsSchema = z.object({
  operatorMarkMode: z.enum(["code", "oib"]),
  /** Seller's VAT/OIB used on invoices (normally the authority OIB). */
  sequenceMode: z.literal("per_premises_device"),
});
export type FiscalSettings = z.infer<typeof fiscalSettingsSchema>;

// ---------------------------------------------------------------------------
// Tariff definition (stored as JSON on `tariffs.definition`)

const monthDay = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, { message: "error.invalidDate" });

export const priceBandSchema = z.object({
  /** Upper length bound (inclusive), metres. `null` = and above. */
  upToM: z.number().positive().max(200).nullable(),
  /** Price per day in cents. With `perMetre`, price per metre of length per day. */
  priceCents: nonNegativeCents,
  perMetre: z.boolean().default(false),
});
export type PriceBand = z.infer<typeof priceBandSchema>;

export const seasonSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  nameEn: z.string().max(60).optional(),
  /** Recurring range `MM-DD`..`MM-DD` (inclusive). May wrap the new year. Both null = fallback season. */
  from: monthDay.nullable(),
  to: monthDay.nullable(),
  bands: z.array(priceBandSchema).min(1).max(20),
});
export type Season = z.infer<typeof seasonSchema>;

export const connectionFeeSchema = z.object({
  connection: z.enum(CONNECTIONS),
  mode: z.enum(["per_day", "metered"]),
  priceCents: nonNegativeCents,
  unit: z.enum(["day", "kwh", "m3"]).default("day"),
});
export type ConnectionFee = z.infer<typeof connectionFeeSchema>;

export const dayRuleSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("day24h"), graceMinutes: z.number().int().min(0).max(720) }),
  z.object({
    mode: z.literal("nights"),
    checkoutHour: z.number().int().min(0).max(23),
    graceMinutes: z.number().int().min(0).max(720),
  }),
]);
export type DayRule = z.infer<typeof dayRuleSchema>;

export const tariffDefinitionSchema = z
  .object({
    seasons: z.array(seasonSchema).min(1).max(12),
    multihullMultiplier: z.number().min(1).max(5),
    minimumChargeCents: nonNegativeCents,
    connectionFees: z.array(connectionFeeSchema).max(10),
    vatRatePercent: z.number().min(0).max(50),
    pricesIncludeVat: z.boolean(),
    rounding: z.enum(["cent", "ten_cents", "fifty_cents", "euro"]),
    dayRule: dayRuleSchema,
  })
  .refine((d) => d.seasons.filter((s) => s.from === null || s.to === null).length <= 1, {
    message: "tariff.error.oneFallback",
    path: ["seasons"],
  })
  .refine((d) => d.seasons.every((s) => (s.from === null) === (s.to === null)), {
    message: "tariff.error.seasonRange",
    path: ["seasons"],
  });
export type TariffDefinition = z.infer<typeof tariffDefinitionSchema>;

export const DEFAULT_TARIFF: TariffDefinition = {
  seasons: [
    {
      id: "high",
      name: "Visoka sezona",
      nameEn: "High season",
      from: "06-01",
      to: "09-30",
      bands: [
        { upToM: 8, priceCents: 3000, perMetre: false },
        { upToM: 12, priceCents: 4500, perMetre: false },
        { upToM: 15, priceCents: 6500, perMetre: false },
        { upToM: 20, priceCents: 9000, perMetre: false },
        { upToM: null, priceCents: 13000, perMetre: false },
      ],
    },
    {
      id: "low",
      name: "Niska sezona",
      nameEn: "Low season",
      from: null,
      to: null,
      bands: [
        { upToM: 8, priceCents: 2000, perMetre: false },
        { upToM: 12, priceCents: 3000, perMetre: false },
        { upToM: 15, priceCents: 4200, perMetre: false },
        { upToM: 20, priceCents: 6000, perMetre: false },
        { upToM: null, priceCents: 8500, perMetre: false },
      ],
    },
  ],
  multihullMultiplier: 1.5,
  minimumChargeCents: 1500,
  connectionFees: [
    { connection: "16A", mode: "per_day", priceCents: 500, unit: "day" },
    { connection: "32A", mode: "per_day", priceCents: 900, unit: "day" },
    { connection: "water", mode: "per_day", priceCents: 300, unit: "day" },
  ],
  vatRatePercent: 13,
  pricesIncludeVat: true,
  rounding: "cent",
  dayRule: { mode: "day24h", graceMinutes: 120 },
};
